import {
  lockBodyScroll,
  unlockBodyScroll,
} from '@/globals/typescript/utils/utils'
import { defineStore } from 'pinia'

type ModalPayload = Record<string, unknown>

export type ModalEntry = {
  id: string
  name: string
  props?: ModalPayload
  width?: 'sm' | 'md' | 'lg'
  useEasyClose?: boolean
  /**
   * Hides ModalHost's own top-right X entirely, for a modal whose only exits
   * are its own explicit buttons (e.g. UnsavedChangesModal's Annuller/Fortsæt).
   */
  hideCloseButton?: boolean
  /**
   * Set on a layer that only exists on behalf of another entry — the unsaved
   * changes confirmation for the modal being closed. Closing the parent by id
   * closes its dependants with it, so a stale confirmation never outlives the
   * modal it is about.
   */
  parentID?: string
}

export const useModalStore = defineStore('modal', {
  state: () => ({
    stack: [] as ModalEntry[],
    scrollLockCount: 0,
  }),

  actions: {
    open({
      name,
      props = {},
      width = 'sm',
      useEasyClose = true,
      hideCloseButton = false,
      parentID,
    }: {
      name: string
      props?: ModalPayload
      width?: 'sm' | 'md' | 'lg'
      useEasyClose?: boolean
      hideCloseButton?: boolean
      parentID?: string
    }) {
      const id = `${name}:${Date.now()}:${Math.random().toString(16).slice(2)}`
      this.stack.push({
        id,
        name,
        props,
        width,
        useEasyClose,
        hideCloseButton,
        parentID,
      })

      this.scrollLockCount++
      if (this.scrollLockCount === 1) lockBodyScroll()

      return id
    },

    closeTop() {
      if (!this.stack.length) return
      this.stack.pop()

      this.scrollLockCount = Math.max(0, this.scrollLockCount - 1)
      if (this.scrollLockCount === 0) unlockBodyScroll()
    },

    /**
     * Closes one entry by id — wherever it sits in the stack — together with
     * every entry that depends on it. Unlike closeTop(), this cannot pop an
     * unrelated layer: a modal that stays mounted beneath a later entry (a
     * pending save under an unsaved-changes confirmation) closes itself, not
     * whatever happens to be on top.
     */
    closeEntry(id: string) {
      const closingIDs = new Set([id])
      let grew = true
      while (grew) {
        grew = false
        for (const entry of this.stack) {
          if (
            entry.parentID &&
            closingIDs.has(entry.parentID) &&
            !closingIDs.has(entry.id)
          ) {
            closingIDs.add(entry.id)
            grew = true
          }
        }
      }

      const remaining = this.stack.filter((entry) => !closingIDs.has(entry.id))
      const removedCount = this.stack.length - remaining.length
      if (!removedCount) return
      this.stack = remaining

      this.scrollLockCount = Math.max(0, this.scrollLockCount - removedCount)
      if (this.scrollLockCount === 0) unlockBodyScroll()
    },

    closeAll() {
      this.stack = []
      this.scrollLockCount = 0
      unlockBodyScroll()
    },
  },

  getters: {
    top: (s) => (s.stack.length ? s.stack[s.stack.length - 1] : null),
  },
})
